import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ChangeCellphoneDialog from './change-cellphone-dialog';
import Countdown from './countdown';
import SupportContactMessage from './support-contact-message';
import { Input } from '../../Shared/BsInput';
import { VerifyCellphoneSchema } from '../../../validation-schemas';
import { useCountdown } from '../../../hooks/useCountdown';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    boxShadow: 'none',
    textAlign: 'center',
  },
  btnContainer: {
    textAlign: 'right',
  },
  actions: {
    textAlign: 'center',
  },
  margin: {
    marginTop: theme.spacing(2),
  },
}));

const RenderForm = ({
  values,
  errors,
  handleChange,
  handleSubmit,
  isSubmitting,
  isValid,
  t,
}) => {
  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <Input
        type="code"
        value={values.code}
        onChange={handleChange}
        name="code"
        label={t('Verification code')}
        errors={errors}
      />
      <Button
        className="mt-2"
        variant="outlined"
        color="default"
        type="submit"
        disabled={isSubmitting || !isValid}
        fullWidth
      >
        {t('Verify cellphone')}
      </Button>
    </form>
  );
};

export default (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const { user, resend, verify, change } = props;

  const limit = user.cellphone_verification_limit;
  const blocked = !!(limit && limit.blocked);
  const cooldownTarget = limit && !limit.blocked ? limit.cooldown_until : null;
  const secondsRemaining = useCountdown(cooldownTarget);
  const canRequestCode = !blocked && secondsRemaining <= 0;

  const handleClose = () => setDialogOpen(false);
  // RF03: o link "clique aqui" sempre abre o modal, mesmo em cooldown - o
  // proprio modal exibe o estado de cooldown/bloqueio (RF04).
  const handleOpen = () => setDialogOpen(true);

  const requestCode = () => {
    if (!canRequestCode) {
      return;
    }

    resend();
  };

  return (
    <>
      <ChangeCellphoneDialog
        opened={dialogOpen}
        handleClose={handleClose}
        user={user}
        onSubmit={async (values) => {
          // RF05: aguarda a promise antes de fechar. change() resolve para
          // `true` em caso de sucesso e `false` quando a request foi barrada
          // por rate-limit (cooldown/bloqueio) - nesse caso o modal permanece
          // aberto e re-renderiza com o novo cellphone_verification_limit
          // (RF04). Erros reais de validacao/rede continuam sendo propagados
          // para o try/catch do Formik do proprio dialog.
          const changed = await change(values);

          if (changed) {
            handleClose();
          }
        }}
      />
      <Paper className={classes.paper} square>
        <Typography align="center">
          {t('We will now verify your cellphone number')}
        </Typography>
        <Typography align="center" variant="h6">
          {user.cellphone}
        </Typography>
        <Typography align="center">
          {t('We will send you a 4 digit verification code via SMS')}
        </Typography>

        {blocked ? (
          // Decisao pragmatica do Orquestrador (a pedido do Bruno): a tela
          // principal nao tem mais early-return de tela cheia para o estado
          // bloqueado (a mensagem de suporte agora so aparece dentro do
          // modal - RF04). Sem isso, o botao de reenvio ficaria "morto"
          // (nem desabilitado com motivo, nem funcional - um clique bateria
          // no backend e tomaria 429 sem explicacao nenhuma). Reaproveita o
          // mesmo SupportContactMessage usado no modal, no lugar do botao.
          //
          // Enquanto o modal esta aberto ele ja mostra essa mesma mensagem
          // por completo (RF04), entao ocultamos a instancia da tela
          // principal nesse momento para nao duplicar o aviso na tela.
          !dialogOpen && <SupportContactMessage supportPhone={limit.support_phone} />
        ) : (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={requestCode}
              className={classes.margin}
              disabled={!canRequestCode}
            >
              {t('I am ready, send me the verification code')}
            </Button>
            {secondsRemaining > 0 && <Countdown seconds={secondsRemaining} />}
          </>
        )}
        <hr />
        <Grid container spacing={2} justify="center">
          <Grid item xs={12} sm={8} md={6}>
            <Formik
              initialValues={{
                code: '',
              }}
              validationSchema={VerifyCellphoneSchema}
              isInitialValid={false}
              enableReinitialize={true}
              onSubmit={
                async (values, { setSubmitting, setErrors }) => {
                  try {
                    await verify(values);
                  } catch (err) {
                    if (err.response && err.response.data.errors) {
                      setErrors(err.response.data.errors);
                    }

                    setSubmitting(false);
                  }
                }
              }
              render={(p) => <RenderForm {...p} {...{ t }} />}
            />
          </Grid>
        </Grid>

        <Typography align="center" className={classes.margin}>
          {t('If you made a mistake or want to change your phone number')},&nbsp;
          <a
            href="javascript:void(0)"
            onClick={handleOpen}
          >
            {t('click here')}
          </a>
        </Typography>
      </Paper>
    </>
  );
}
